/**
 * Kontextuelle Onboarding-Tipps (PR2) — einmalige Coachmarks, die beim ERSTEN
 * Betreten eines transienten Features erscheinen (Bulk-Bar, Quick-Look, Share).
 *
 * Anders als die Haupt-Tour (Backend `onboarded_at`, once-per-user) sind die
 * Tipps **leichtgewichtig per Gerät** in localStorage (`gf-tip-<key>`) persistiert
 * — ein Tipp, der auf einem neuen Gerät einmal erneut erscheint, ist harmlos.
 *
 * Single-Step-Spotlight via dem geteilten driver.js-Wrapper (`startTour`). Ein
 * Modul-Lock stellt sicher, dass nie zwei Tipp-Spotlights gleichzeitig laufen.
 */
import { useEffect, useRef } from 'react';
import { startTour, type DriverFactory, type TourStepDef } from './onboarding-tour';

const TIP_PREFIX = 'gf-tip-';
const MIN_VIEWPORT_WIDTH = 640; // Desktop-first (Anker liegen in Desktop-Shell/Overlays)

// Modul-Lock: nur EIN Tipp-Spotlight gleichzeitig (verhindert driver.js-Konflikt,
// falls zwei Bedingungen quasi-gleichzeitig true werden).
let tipShowing = false;

export function hasSeenTip(key: string): boolean {
  try {
    return localStorage.getItem(TIP_PREFIX + key) === '1';
  } catch {
    return false;
  }
}

export function markTipSeen(key: string): void {
  try {
    localStorage.setItem(TIP_PREFIX + key, '1');
  } catch {
    /* localStorage nicht verfügbar (private mode etc.) — Tipp zeigt ggf. erneut, harmlos. */
  }
}

/** Test-Helper. NUR für Vitest. */
export function _resetTipLockForTesting(): void {
  tipShowing = false;
}

export interface ContextualTipOpts {
  /** data-tour-Key des Ziel-Elements. */
  anchor: string;
  title: string;
  description: string;
  side?: TourStepDef['side'];
  /** Test-Seam: injizierbare driver.js-Factory. */
  factory?: DriverFactory;
}

/**
 * Zeigt EINMAL einen Single-Step-Spotlight-Tipp am `anchor`, sobald `active`
 * erstmals true wird (und der Anker im DOM ist + der Tipp noch nicht gesehen
 * wurde + Desktop-Viewport + kein anderer Tipp läuft). Danach persistiert in
 * localStorage → kein Re-Fire, auch über Reloads.
 */
export function useContextualTip(
  key: string,
  active: boolean,
  opts: ContextualTipOpts,
): void {
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current || !active) return;
    if (hasSeenTip(key)) {
      firedRef.current = true; // schon gesehen → nie wieder versuchen
      return;
    }
    if (window.innerWidth < MIN_VIEWPORT_WIDTH) return; // Desktop-first
    if (tipShowing) return; // anderer Tipp läuft → bei nächstem active-Toggle erneut

    const started = startTour({
      steps: [
        {
          anchor: opts.anchor,
          title: opts.title,
          description: opts.description,
          side: opts.side,
        },
      ],
      showProgress: false, // Single-Step → kein „1 / 1"
      factory: opts.factory,
      onEnd: () => {
        tipShowing = false;
      },
    });

    if (started) {
      // Auf SHOW markieren (nicht erst on-dismiss) → kein Re-Fire, auch wenn der
      // User wegnavigiert ohne „Fertig" zu klicken.
      firedRef.current = true;
      tipShowing = true;
      markTipSeen(key);
    }
  }, [key, active, opts.anchor, opts.title, opts.description, opts.side, opts.factory]);
}
